import { Socket } from "@supabase/phoenix";
import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";

type RealtimeStatusCallback = NonNullable<
  Parameters<RealtimeChannel["subscribe"]>[0]
>;

type PartialTransport = {
  close?: unknown;
  onopen?: unknown;
  onerror?: unknown;
  onmessage?: unknown;
  onclose?: unknown;
};

type GuardableSocket = {
  conn?: PartialTransport | null;
  teardown: (
    callback?: () => void,
    code?: number,
    reason?: string,
  ) => unknown;
  replaceTransport: (transport: unknown) => unknown;
};

type GuardableSocketConstructor = {
  prototype: GuardableSocket & Record<PropertyKey, unknown>;
};

const PHOENIX_CLOSE_GUARD = Symbol.for(
  "bookaclassic.supabase.phoenix-close-guard",
);

function detachPartialTransport(
  socket: GuardableSocket,
  transport: PartialTransport,
): void {
  const noop = () => {};
  for (const key of ["onopen", "onerror", "onmessage", "onclose"] as const) {
    try {
      transport[key] = noop;
    } catch {
      // A partially initialized host object may reject a handler write.
    }
  }
  if (socket.conn === transport) socket.conn = null;
}

/**
 * Phoenix calls `conn.close()` during transport replacement and teardown.
 * Browser lifecycle transitions can leave a partial transport without a
 * callable close method, so detach that malformed transport before delegating
 * healthy sockets to Phoenix's original implementation.
 */
export function installPhoenixTransportCloseGuard(
  SocketConstructor: GuardableSocketConstructor = Socket as unknown as GuardableSocketConstructor,
): void {
  const prototype = SocketConstructor.prototype;
  if (prototype[PHOENIX_CLOSE_GUARD]) return;

  const originalTeardown = prototype.teardown;
  const originalReplaceTransport = prototype.replaceTransport;

  prototype.teardown = function guardedTeardown(
    this: GuardableSocket,
    callback?: () => void,
    code?: number,
    reason?: string,
  ): unknown {
    const transport = this.conn;
    if (transport && typeof transport.close !== "function") {
      detachPartialTransport(this, transport);
      callback?.();
      return;
    }
    return originalTeardown.call(this, callback, code, reason);
  };

  prototype.replaceTransport = function guardedReplaceTransport(
    this: GuardableSocket,
    transport: unknown,
  ): unknown {
    const activeTransport = this.conn;
    if (activeTransport && typeof activeTransport.close !== "function") {
      detachPartialTransport(this, activeTransport);
    }
    return originalReplaceTransport.call(this, transport);
  };

  Object.defineProperty(prototype, PHOENIX_CLOSE_GUARD, {
    configurable: false,
    enumerable: false,
    value: true,
    writable: false,
  });
}

// Install before the shared Supabase client opens its first realtime socket.
installPhoenixTransportCloseGuard();

/**
 * Realtime is an optional freshness path. Channel creation, listener
 * registration, and subscription can all throw synchronously while a browser
 * is disconnecting, so contain the failure and leave fetch-driven refreshes
 * available.
 */
export function subscribeToOptionalRealtimeChannel(
  client: SupabaseClient,
  createChannel: () => RealtimeChannel,
  configure: (channel: RealtimeChannel) => void,
  onStatus?: RealtimeStatusCallback,
): RealtimeChannel | null {
  let channel: RealtimeChannel | null = null;
  try {
    channel = createChannel();
    configure(channel);
    if (onStatus) channel.subscribe(onStatus);
    else channel.subscribe();
    return channel;
  } catch {
    if (channel) safeRemoveRealtimeChannel(client, channel);
    return null;
  }
}

/**
 * React cleanups cannot await Supabase's asynchronous channel removal. Absorb
 * both immediate throws and rejected removal promises so optional realtime
 * teardown cannot become a browser-level error.
 */
export function safeRemoveRealtimeChannel(
  client: SupabaseClient,
  channel: RealtimeChannel | null | undefined,
): void {
  if (!channel) return;
  try {
    const result = client.removeChannel(channel);
    if (result && typeof (result as PromiseLike<unknown>).then === "function") {
      void Promise.resolve(result).catch(() => {});
    }
  } catch {
    // Fetch/query invalidation remains the authoritative recovery path.
  }
}